import AddBoxIcon from '@mui/icons-material/AddBox'
import { Avatar, CssBaseline } from '@mui/material'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React, { useState, useEffect } from 'react'
import { useRecoilState } from 'recoil'

import { AppBarR as PersistentDrawerRight } from '../components/common'
import { getOfficesManagedByUser, addOfficeNotification } from '../db/db'
import { sessionState } from '../state'
import Database from '../types/Database'

const OfficeNotifications: React.FC = () => {
  const [userState] = useRecoilState(sessionState)
  const user = userState.user
  const [offices, setOffices] = useState<
    Array<Database.Office & { id: string }>
  >([])
  const [selectedOffice, setSelectedOffice] = useState('')
  const [notificationText, setNotificationText] = useState('')
  const [notificationDate, setNotificationDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchOffices = async () => {
      console.log('Fetching offices for user:', user?.uid, user?.email)
      if (user?.uid && user?.email) {
        try {
          const fetchedOffices = await getOfficesManagedByUser(user.uid)
          console.log('Fetched offices:', fetchedOffices)
          setOffices(fetchedOffices)
        } catch (error) {
          console.error('Error fetching offices:', error)
          setOffices([])
        }
      } else {
        console.log('No user or user missing uid/email')
        setOffices([])
      }
    }

    fetchOffices()
  }, [user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    console.log('offices:', offices)
    console.log('selected office:', selectedOffice)
    console.log('notification date (raw):', notificationDate)

    try {
      if (user && user.uid) {
        console.log('userid:', user.email)
        // Ensure date is in YYYY-MM-DD format (it should already be from the input)
        const dateStr = notificationDate
        console.log('notification date (formatted):', dateStr)

        await addOfficeNotification(
          selectedOffice,
          notificationText,
          dateStr,
          user.uid
        )
        setSuccess(true)
        setNotificationText('')
        setNotificationDate('')
        setTimeout(() => setSuccess(false), 3000)
      }
    } catch (err) {
      console.error('Error adding notification:', err)
      setError('Failed to add notification. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <PersistentDrawerRight />
      <CssBaseline />
      <Container
        component='main'
        maxWidth='sm'
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100vh'
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 600,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(10px)',
            p: 4,
            mb: 4
          }}
        >
          <Container maxWidth='sm' sx={{ mt: 10 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 32,
                    height: 32
                  }}
                >
                  <AddBoxIcon fontSize='small' />
                </Avatar>
                <Typography
                  component='h1'
                  variant='h6'
                  sx={{ fontWeight: 600 }}
                >
                  Add Days notes
                </Typography>
              </Box>

              <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                Create notifications for specific dates in your offices
              </Typography>
              <Box
                component='form'
                noValidate
                onSubmit={handleSubmit}
                sx={{ mt: 2 }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel id='office-select-label'>
                        Select Office
                      </InputLabel>
                      <Select
                        labelId='office-select-label'
                        id='office-select'
                        value={selectedOffice}
                        label='Select Office'
                        onChange={e => setSelectedOffice(e.target.value)}
                      >
                        <MenuItem value=''>
                          <em>--Select--</em>
                        </MenuItem>
                        {offices.map(office => (
                          <MenuItem key={office.oid} value={office.oid}>
                            {office.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id='notificationText'
                      label='Notification Text'
                      value={notificationText}
                      onChange={e => setNotificationText(e.target.value)}
                      multiline
                      minRows={4}
                      helperText='Supports markdown: **bold**, # Heading, ## Subheading, [link](url)'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id='notificationDate'
                      label='Date'
                      type='date'
                      value={notificationDate}
                      onChange={e => setNotificationDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type='submit'
                      fullWidth
                      variant='contained'
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        background:
                          'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.6)'
                        },
                        '&:disabled': {
                          background: 'rgba(0,0,0,0.12)',
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }}
                      disabled={loading}
                      color='primary'
                    >
                      {loading ? 'Adding...' : 'Add Notification'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              {success && (
                <Alert severity='success' sx={{ mt: 3, borderRadius: 2 }}>
                  Notification added!
                </Alert>
              )}
              {error && (
                <Alert severity='error' sx={{ mt: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </Container>
        </Box>
      </Container>
    </>
  )
}

export default OfficeNotifications
